import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { DateTimeRangePicker } from '../components/ui/date-time-range-picker';
import { Button } from '../components/ui/button';
import { TrendingUp, DollarSign, MinusCircle, X } from 'lucide-react';
import { api } from '../lib/api';
import { formatPrice } from '../lib/utils';
import type { ReportData } from '../types/car';

type DateRange = { from: Date | undefined; to?: Date | undefined };

export default function Report() {
  const { t } = useLanguage();
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  useEffect(() => {
    fetchReport();
  }, [dateRange]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange?.from) params.append('fromDate', dateRange.from.toISOString());
      if (dateRange?.to) {
        const end = new Date(dateRange.to);
        end.setHours(23, 59, 59, 999);
        params.append('toDate', end.toISOString());
      }
      const report = await api.get(`/api/report?${params.toString()}`);
      setData(report as ReportData);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-48 rounded-lg" />
          <span className="text-sm text-slate-500">{t('report.loading')}</span>
        </div>
      </div>
    );
  }

  const report = data ?? {
    gmvUSD: 0,
    gmvIQD: 0,
    totalExpensesUSD: 0,
    totalExpensesIQD: 0,
    revenueUSD: 0,
    revenueIQD: 0,
    salesCount: 0,
    expensesCount: 0,
    dateRange: null,
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('report.title')}</h1>
          <p className="text-slate-500 text-sm mt-0.5">{t('report.subtitle')}</p>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle>{t('report.dateRange')}</CardTitle>
            <CardDescription>{t('report.dateRangeDescription')}</CardDescription>
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <Label>{t('report.range')}</Label>
                <DateTimeRangePicker value={dateRange} onValueChange={setDateRange} className="w-full mt-1 max-w-sm" />
              </div>
              {(dateRange?.from ?? dateRange?.to) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-slate-200 text-slate-700 hover:bg-slate-50 shrink-0"
                  onClick={() => setDateRange(undefined)}
                >
                  <X className="h-4 w-4 mr-2" />
                  {t('report.clearFilter')}
                </Button>
              )}
            </div>
          </CardHeader>
        </Card>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="rounded-2xl border border-slate-200 shadow-sm bg-white">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">{t('report.gmvCard')}</CardTitle>
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  <div className="text-xl font-semibold text-emerald-600 tabular-nums">{formatPrice(report.gmvUSD, 'USD')}</div>
                  <div className="text-xl font-semibold text-emerald-600 tabular-nums">{formatPrice(report.gmvIQD, 'IQD')}</div>
                </div>
                <p className="text-xs text-slate-500 mt-1">{report.salesCount} sale(s)</p>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border border-slate-200 shadow-sm bg-white">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">{t('report.totalExpenses')}</CardTitle>
                <MinusCircle className="h-5 w-5 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  <div className="text-xl font-semibold text-red-600 tabular-nums">{formatPrice(report.totalExpensesUSD, 'USD')}</div>
                  <div className="text-xl font-semibold text-red-600 tabular-nums">{formatPrice(report.totalExpensesIQD, 'IQD')}</div>
                </div>
                <p className="text-xs text-slate-500 mt-1">{report.expensesCount} expense(s)</p>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border border-slate-200 shadow-sm bg-white">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">{t('report.revenueCard')}</CardTitle>
                <DollarSign className="h-5 w-5 text-slate-600" />
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  <div className={`text-xl font-semibold tabular-nums ${report.revenueUSD >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatPrice(report.revenueUSD, 'USD')}
                  </div>
                  <div className={`text-xl font-semibold tabular-nums ${report.revenueIQD >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatPrice(report.revenueIQD, 'IQD')}
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">{t('report.gmvMinusExpenses')}</p>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
